<template>
  <v-row class="newsform match-height">
    <v-col cols="12">
      <v-card>
        <v-card-title>{{ getPageTitle() }} </v-card-title>
        <v-card-text>
          <v-form v-on:submit.prevent="submit" ref="form">
            <label for="image"> {{ $t("Kuva") }}</label>
            <br />
            <small class="form-text text-muted">
              {{
                $t(
                  "Raahaa kuva tähän tai klikkaa tästä ja valitse kuva tiedostoista."
                )
              }}
            </small>
            <br />
            <croppa initial-size="contain" v-model="myCroppa" :width="600" :height="400" accept=".jpeg,.jpg,.png"
              :disabled="croppaEnabled === false" @file-type-mismatch="onFileTypeMismatch"
              :placeholder="$t('Lisää kuva klikkaamalla tai raahaamalla kuva tähän')" :placeholder-font-size="20"
              :initial-image="news.image" @image-remove="removeImage" @new-image-drawn="imageLoaded">
            </croppa>
            <CroppaControls :croppa="myCroppa" v-if="showCroppaControls" />
            <div v-if="news.image">
              <small class="form-text text-muted">{{
                $t("Jos haluat päivittää kuvan, poista ensin olemassa oleva kuva")
                }}</small>
            </div>

            <div class="mb-8">
              <v-radio-group v-model="news.group" :label="$t('Julkaise ryhmään')" :rules="inputRules.group"
                hide-details="auto">
                <!-- <v-radio v-for="n in 3" :key="n" :label="`Radio ${n}`" :value="n"></v-radio> -->
                <v-radio v-for="group in user.admin_groups" :key="group.id" required :value="group.id"
                  :label="group.name" />
              </v-radio-group>
            </div>

            <div class="mb-4">
              <v-datetime-picker dateFormat="dd.MM.yyyy" :label="$t('Julkaisuaika')" v-model="news.release_date"
                :date-picker-props="{
                  firstDayOfWeek: 1
                }" :time-picker-props="{
                  format: '24hr',
                }" :textFieldProps="{
                  outlined: 'outlined',
                  'hide-details': '',
                }">
                <template slot="dateIcon">
                  <v-icon>
                    {{ icons.mdiCalendar }}
                  </v-icon>
                </template>
                <template slot="timeIcon">
                  <v-icon>
                    {{ icons.mdiClockOutline }}
                  </v-icon>
                </template>
              </v-datetime-picker>
              <v-input type="hidden" :rules="inputRules.publishDate" v-model="news.release_date"></v-input>
            </div>
            <v-card outlined :key="forceUpdateTranslationsKey">
              <v-tabs v-model="tab" grow>
                <v-tab v-for="lang in tenant.languages" :key="'langtab-' + lang.code">
                  <img :src="lang.icon" style="max-height: 20px" />
                  &nbsp;&nbsp;{{ lang.name }}
                  &nbsp;
                  <v-icon v-if="langOk(news.translations, lang.code, ['title', 'text'])
                  " color="success">
                    {{ icons.mdiCheck }}
                  </v-icon>
                  <v-icon v-else color="error">
                    {{ icons.mdiBlockHelper }}
                  </v-icon>
                </v-tab>
              </v-tabs>

              <v-tabs-items v-model="tab" style="overflow: visible">
                <v-tab-item v-for="lang in tenant.languages" :key="'langtabitem-' + lang.code">
                  <div style="padding: 20px">
                    <img :src="lang.icon" style="max-height: 14px" />
                    <br />
                    <br />
                    <v-text-field outlined :id="'news-title-' + lang.code" v-model="news.translations[lang.code].title"
                      :label="$t('Otsikko')" hide-details class="mb-4" />
                    <div>
                      <quill-editor height="400" ref="myQuillEditor" v-model="news.translations[lang.code].text"
                        @ready="onEditorReady($event)" :options="editorOption" />
                    </div>
                  </div>

                  <div class="mx-5" v-if="tenant.settings.auto_translations_enabled">
                    <AutoTranslateButton :current="lang" :languages="tenant.languages" :is-translating="isTranslating"
                      @click="translate" translation-errors="translationErrors" :errors="translationErrors">
                    </AutoTranslateButton>
                  </div>

                </v-tab-item>
              </v-tabs-items>
              <v-input class="ml-4 mb-4" hidden hide-details="auto" :rules="[validateLangs]"></v-input>
            </v-card>
            <template v-if="tenant.settings.tags_enabled">
              <br />
              <TagSelector v-model="news.tags" />
            </template>
            <br />
            <v-card outlined>
              <v-card-title>{{ $t("Liitteet") }}</v-card-title>
              <v-card-text>
                <v-list>
                  <v-list-item v-for="attachment in news.attachments" :key="attachment.id">
                    <v-list-item-icon>
                      <v-icon color="primary">
                        {{ icons.mdiAttachment }}
                      </v-icon>
                    </v-list-item-icon>
                    <v-list-item-content style="cursor: pointer" @click="downloadAttachment(attachment)">
                      {{ attachment.name }}
                    </v-list-item-content>
                    <v-list-item-icon @click="removeAttachment(attachment)" style="cursor: pointer">
                      <v-icon color="secondary">
                        {{ icons.mdiDeleteOutline }}
                      </v-icon>
                    </v-list-item-icon>
                  </v-list-item>
                </v-list>
                <v-file-input outlined ref="attachmentInput" :label="$t('Lisää uusi liitetiedosto')"
                  :loading="isUploading" @change="saveAttachment"
                  accept=".jpeg,.jpg,.png, .pdf, .doc, .docx, .ppt, .pptx, .xlsx, .xls">
                </v-file-input>
              </v-card-text>
            </v-card>
            <br />
            <v-card outlined>
              <v-card-title>{{ $t("Video") }}</v-card-title>
              <v-card-text>
                <v-switch dense hide-details="auto" v-model="hasVideo" :label="$t('Lisää uutiseen video')"
                  @change="news.videoUrl = ''" class="mb-2 mt-0"></v-switch>

                <v-text-field v-if="hasVideo" :label="$t('Videotiedoston url-osoite')" v-model="news.videoUrl"
                  append-outer-icon="mdi-help" outlined>
                </v-text-field>
              </v-card-text>
            </v-card>
            <br />
            <v-card outlined>
              <v-card-title>{{ $t("Kysely") }}</v-card-title>
              <v-card-text>
                <v-switch v-model="hasPoll" inset persistent-hint :label="$t('Lisää julkaisuun kysymys')"></v-switch>
                <br />
                <template v-if="hasPoll">
                  <v-datetime-picker dateFormat="dd.MM.yyyy" :label="$t('Vastausaika päättyy')"
                    v-model="poll.expire_date" :date-picker-props="{
                      firstDayOfWeek: 1
                    }" :time-picker-props="{
                      format: '24hr',
                    }" :textFieldProps="{
                      outlined: 'outlined',
                      'hide-details': '',
                    }">
                    <template slot="dateIcon">
                      <v-icon>
                        {{ icons.mdiCalendar }}
                      </v-icon>
                    </template>
                    <template slot="timeIcon">
                      <v-icon>
                        {{ icons.mdiClockOutline }}
                      </v-icon>
                    </template>
                  </v-datetime-picker>
                  <v-input type="hidden" :rules="inputRules.pollExpireDate" v-model="poll.expire_date"></v-input>

                  <v-switch v-model="poll.anonymous" :disabled="poll.answers > 0"
                    :label="$t('Vastaukset kerätään anonyymisti')" persistent-hint
                    :hint="$t('Tätä asetusta ei voi muuttaa sen jälkeen kun kyselyyn on vastattu')"></v-switch>

                  <v-switch v-model="poll.allow_multiple_answers" class="mt-5 mb-7"
                    :label="$t('Salli useita vastauksia samalta käyttäjältä')" persistent-hint></v-switch>

                  <div>
                    <v-alert type="info" v-if="hasAnswers" small class="mt-2">{{
                      $t(
                        "Kyselyyn on vastattu eikä kysymyksiä voi enää muokata"
                      )
                    }}</v-alert>
                    <h3>
                      {{ $t("Kysymykset") }}
                      <v-menu offset-y>
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn v-if="!hasAnswers" color="primary" fab dark x-small v-bind="attrs" v-on="on">
                            <v-icon> {{ icons.mdiPlus }} </v-icon>
                          </v-btn>
                        </template>
                        <v-list>
                          <v-list-item @click="addQuestion(1)">
                            <v-list-item-title>{{
                              $t("Uusi monivalintakysymys")
                              }}</v-list-item-title>
                          </v-list-item>
                          <v-list-item @click="addQuestion(2)">
                            <v-list-item-title>{{
                              $t("Uusi avoin kysymys")
                              }}</v-list-item-title>
                          </v-list-item>
                        </v-list>
                      </v-menu>
                    </h3>
                    <br />
                    <template v-if="poll.poll_questions.length">
                      <v-tabs v-model="questionsTab" background-color="primary" class="elevation-2" dark
                        :vertical="true" style="min-height: 260px">
                        <v-tab v-for="(question, i) in poll.poll_questions" :key="question.id" :href="`#tab-${i}`">
                          <span>{{ i + 1 }}. {{ $t("Kysymys") }} &nbsp;</span>
                          <v-icon size="20" v-if="question.question_type == 1" color="white">
                            {{ icons.mdiHandPointingUp }}
                          </v-icon>
                          <v-icon size="20" v-else color="white">
                            {{ icons.mdiChatOutline }}
                          </v-icon>
                        </v-tab>

                        <v-tab-item v-for="(question, i) in poll.poll_questions" :key="question.id" :value="'tab-' + i">
                          <v-card flat tile>
                            <v-card-text>
                              <h4 style="margin-bottom: 15px">
                                {{ $t("Kysymys") }}
                              </h4>
                              <div v-for="lang in tenant.languages" :key="'questionlangtabitem-' + lang.code">
                                <v-text-field v-if="!hasAnswers" :append-outer-icon="icons.mdiDeleteOutline"
                                  @click:append-outer="removeQuestion(i)" v-model="question.translations[lang.code].question
                                    " :label="$t('Kirjoita kysymys')">
                                  <img slot="prepend" :src="lang.icon" style="max-height: 14px" />
                                </v-text-field>
                                <v-text-field v-else disabled v-model="question.translations[lang.code].question
                                  " :label="$t('Kirjoita kysymys')">
                                  <img slot="prepend" :src="lang.icon" style="max-height: 14px" />
                                </v-text-field>
                              </div>

                              <template v-if="question.question_type == 1">
                                <h4 v-if="!hasAnswers">
                                  {{ $t("Lisää vastausvaihtoehto") }}
                                  <v-btn color="secondary" x-small fab @click="addOption(question)">
                                    <v-icon>
                                      {{ icons.mdiPlus }}
                                    </v-icon>
                                  </v-btn>
                                </h4>
                                <br />
                                <v-card outlined v-for="(option, i2) in question.question_options" :key="option.id"
                                  style="padding: 10px; margin-top: 5px">
                                  <div v-for="lang in tenant.languages" :key="'questionlangtabitem2-' + lang.code">
                                    <v-text-field v-if="!hasAnswers" :append-outer-icon="icons.mdiDeleteOutline
                                      " @click:append-outer="
                                        removeOption(question, i2)
                                        " v-model="option.translations[lang.code].title
                                          " :label="$t('Kirjoita vastausvaihtoehto')">
                                      <img slot="prepend" :src="lang.icon" style="max-height: 14px" />
                                    </v-text-field>
                                    <v-text-field v-else disabled v-model="option.translations[lang.code].title
                                      " :label="$t('Kirjoita vastausvaihtoehto')">
                                      <img slot="prepend" :src="lang.icon" style="max-height: 14px" />
                                    </v-text-field>
                                  </div>
                                </v-card>
                              </template>
                            </v-card-text>
                          </v-card>
                        </v-tab-item>
                      </v-tabs>
                    </template>
                  </div>
                </template>
                <v-input type="hidden" :rules="inputRules.poll" v-model="poll.poll_questions"></v-input>
                <v-input type="hidden" validate-on-blur :rules="inputRules.pollQuestions"
                  v-model="poll.poll_questions"></v-input>
              </v-card-text>
            </v-card>
            <br>

            <v-switch v-if="tenant.settings.infotv_enabled" v-model="news.infotv" inset persistent-hint
              :hint="$t('Näytä infotv näytöillä')" :label="$t('Infotv')">
            </v-switch>
            <br />

            <v-switch v-model="news.published" inset persistent-hint :hint="news.published
              ? $t('Uutinen julkaistaan julkaisaikana')
              : $t('Uutinen tallennetaan luonnoksena')" :label="$t('Julkaise')">
            </v-switch>
            <br />
            <v-alert color="error" text v-if="!formIsValid">
              {{ $t("Tarkista lomakkeen tiedot") }}
            </v-alert>
            <v-btn color="error" v-if="news.id" @click="removeNews" class="mr-3">{{ $t("Poista") }}</v-btn>
            <v-btn color="primary" @click="submit" :loading="isSubmitting">{{ $t("Tallenna") }}</v-btn>
          </v-form>
        </v-card-text>
      </v-card>
      <v-snackbar v-model="translationCompleted" color="success">
        <template #default>
          <v-icon>{{ icons.mdiCheck }}</v-icon>
          {{ $t('Käännös suoritettu. Varmista käännöksen oikeellisuus ennen tallentamista.') }}
        </template>
      </v-snackbar>
    </v-col>
  </v-row>

</template>

<script>
import { computed, onMounted, reactive, ref } from "@vue/composition-api";
import adminApi from "@/api/admin";
import { useRouter } from "@/@core/utils";
import useAuth from "@/useAuth";
import { useTranslations } from "@/@core/libs/i18n";

import {
  convertTranslationsHtmlToMarkdown,
  convertTranslationMarkdownToHtml,
  getValidTranslations,
  translateObjectTranslations,
  langOk,
} from "@/@core/utils/translation_helpers";
import {
  mdiCheck,
  mdiBlockHelper,
  mdiClockOutline,
  mdiCalendar,
  mdiAttachment,
  mdiDeleteOutline,
  mdiPlus,
  mdiHandPointingUp,
  mdiMap,
  mdiChatOutline,
  mdiArrowRight
} from "@mdi/js";
import "quill/dist/quill.core.css";
import "quill/dist/quill.snow.css";
import "quill/dist/quill.bubble.css";
import { quillEditor } from "vue-quill-editor";
import "vue-croppa/dist/vue-croppa.css";
import download from "downloadjs";
import moment from "moment";
import useMenus from "@/@core/utils/useMenus";
import { i18n } from "@/plugins/i18n";
import ImageUploader from 'quill-image-uploader';
import useMarkdown from "@/@core/utils/useMarkdown";
import Quill from 'quill';
import 'quill-paste-smart';
import AutoTranslateButton from "@/components/AutoTranslateButton.vue";
import TagSelector from "@/components/TagSelector.vue";
import { MAX_FILE_SIZE } from "@core/utils/constants";
import CroppaControls from "@/components/CroppaControls.vue";
Quill.register("modules/imageUploader", ImageUploader);


export default {
  props: ["id", "parent"],
  components: {
    quillEditor,
    AutoTranslateButton,
    TagSelector,
    CroppaControls
  },
  setup(props) {
    const { router, route } = useRouter();
    const { tenant, user } = useAuth();
    const { t } = useTranslations();
    const questionsTab = ref(null);
    const formIsValid = ref(true);
    const attachmentInput = ref(null);
    const { getTenantTerm } = useMenus();
    const tab = ref(null);
    const form = ref(null);
    const isSubmitting = ref(false);
    const croppaEnabled = ref(true);
    const { markdownToHtml } = useMarkdown();
    const forceUpdateTranslationsKey = ref(new Date().toString());
    const isTranslating = ref(false);
    const translationErrors = ref([]);
    const translationCompleted = ref(false);
    const isUploading = ref(false);

    const forceUpdateTranslations = () => {
      forceUpdateTranslationsKey.value = new Date().toString();
    }

    const editorOption = {
      modules: {
        clipboard: {
          keepSelection: true,
          substituteBlockElements: false,
          magicPasteLinks: true,
        },
        toolbar: [
          ["bold", "italic"],
          [{ 'list': 'ordered' }, { 'list': 'bullet' }],
          ["link", "image"],
        ],
        imageUploader: {
          upload: (file) => {
            return new Promise(async (resolve, reject) => {

              const res = await adminApi.uploadImage(file);

              if (res.ok) {
                resolve(res.data);
              }
              else {
                reject("Image upload failed");
              }
            });
          },
        },
      },
    }

    const myCroppa = ref({});
    const myCroppaInitialMedadata = {
      startX: 0,
      startY: 0,
      scale: 1,
      orientation: 1,
    };

    const news = reactive({
      id: null,
      group: null,
      translations: {},
      release_date: "",
      expire_date: "",
      image: "",
      published: true,
      poll: null,
      videoUrl: null,
      infotv: false,
      attachments: [],
      tags: []
    });
    const imageRemoved = ref(false);
    // const attachments = ref([]);
    const hasVideo = ref(false);
    const hasPoll = ref(false);

    const poll = reactive({
      id: null,
      translations: {},
      release_date: "",
      expire_date: "",
      poll_questions: [],
      answers: 0,
      allow_multiple_answers: false,
      anonymous: true
    });
    const hasAnswers = computed(() => {
      return poll.answers > 0;
    });

    tenant.value.languages.forEach((lang) => {
      news.translations[lang.code] = {
        title: "",
        text: "",
      };
    });

    const inputRules = {
      group: [(v) => !!v || i18n.t("Ryhmä on pakollinen")],
      translations: [
        (v) =>
          validateTranslations() || i18n.t("Otsikko ja teksti on pakollinen"),
      ],
      publishDate: [(v) => !!v || i18n.t("Julkaisuaika on pakollinen")],
      pollExpireDate: [
        (v) => !!v || i18n.t("Vastausaika päättyy on pakollinen"),
      ],
      poll: [(v) => validatePoll(v) || i18n.t("Kysymys puuttuu")],
      pollQuestions: [
        (v) => validateQuestions(v) || i18n.t("Tarkista kysymykset"),
      ],
    };

    const fetchNews = async (id) => {
      const response = await adminApi.getNews(id);
      if (response.ok) {
        news.id = response.data.id;
        news.group = response.data.group;
        news.published = response.data.published;
        news.infotv = response.data.infotv;
        news.ui_order = response.data.ui_order;
        // news.videoId = response.data.videoId;
        const releaseDate = moment(response.data.release_date);
        news.release_date = releaseDate.format("DD.MM.YYYY HH:mm");
        news.poll = response.data.poll;

        // Translations
        news.translations = {};
        tenant.value.languages.forEach((lang) => {
          if (response.data.translations[lang.code]) {
            news.translations[lang.code] =
              response.data.translations[lang.code];
          } else {
            news.translations[lang.code] = {
              title: "",
              text: "",
            };
          }
        });
        convertTranslationMarkdownToHtml(news.translations, ["text"]);

        // Image
        if (response.data.image) {
          const fullSize = response.data.image["full_size"];
          if (fullSize) {
            news.image = fullSize;
            croppaEnabled.value = false;
          }
        }

        // Attachments
        response.data.attachments.forEach((attachment) => {
          if (attachment.attachment_type == 2) {
            hasVideo.value = true;
            news.videoUrl = attachment.url;
          } else {
            news.attachments.push(attachment);
          }
        });

        // myCroppa
        if (news.image) {
          myCroppa.value.refresh();
        }

        // Tags
        if (response.data.tags) {
          news.tags = response.data.tags;
        }

        if (news.poll) {
          await fetchPoll(news.poll);
          hasPoll.value = true;
        }
      } else {
        alert(response.error);
      }
    };

    const fetchPoll = async (pollId) => {
      const response = await adminApi.getPoll(pollId);

      if (response.ok) {
        poll.id = response.data.id;
        poll.group = response.data.group;
        poll.answers = response.data.answers;
        poll.allow_multiple_answers = response.data.allow_multiple_answers;
        poll.anonymous = response.data.anonymous;

        poll.translations = {};
        tenant.value.languages.forEach((lang) => {
          if (response.data.translations[lang.code]) {
            poll.translations[lang.code] =
              response.data.translations[lang.code];
          } else {
            poll.translations[lang.code] = {
              title: "",
              description: "",
            };
          }
        });
        convertTranslationMarkdownToHtml(poll.translations, ["description"]);

        response.data.poll_questions.forEach((question) => {
          let q = question;
          tenant.value.languages.forEach((lang) => {
            if (!q.translations[lang.code]) {
              q.translations[lang.code] = {
                question: "",
              };
            }

            q.question_options.forEach((option) => {
              if (!option.translations[lang.code]) {
                option.translations[lang.code] = {
                  title: "",
                };
              }
            });
          });

          poll.poll_questions.push(q);
        });

        poll.release_date = moment(response.data["release_date"]).format(
          "DD.MM.YYYY HH:mm"
        );
        poll.expire_date = moment(response.data["expire_date"]).format(
          "DD.MM.YYYY HH:mm"
        );
      } else {
        alert(response.error);
      }
    };

    const submit = async () => {
      formIsValid.value = true;
      if (form.value.validate()) {
        isSubmitting.value = true;
        const saveNewsRequest = Object.assign({}, news);

        // Translations
        saveNewsRequest.translations = getValidTranslations(news.translations, [
          "title",
          "text",
        ]);
        saveNewsRequest.attachments = news.attachments.map((a) => a.id);


        // Image
        let newImageName = "";
        let imageBlob = null;
        let removeImage = false;
        if (myCroppa.value.hasImage()) {
          if (myCroppa.value.chosenFile) {
            imageBlob = await myCroppa.value.promisedBlob(
              myCroppa.value.chosenFile.type,
              0.8
            );
            newImageName = myCroppa.value.chosenFile.name;
          }
        } else if (imageRemoved.value) {
          removeImage = true;
        }

        if (hasPoll.value) {
          const pollResponse = await submitPoll();
          if (pollResponse.ok) {
            saveNewsRequest.poll = pollResponse.data.id;
          } else {
            alert(pollResponse.error);
            isSubmitting.value = false;
            return;
          }
        }

        // convertTranslationsHtmlToMarkdown(saveNewsRequest.translations, [
        //   "text",
        // ]);

        const response = await adminApi.saveNews(
          saveNewsRequest,
          imageBlob,
          newImageName,
          removeImage
        );

        if (response.ok) {
          router.push({ name: "admin-news-list" });
        } else {
          alert(response.error);
        }
      } else {
        formIsValid.value = false;
      }

      isSubmitting.value = false;
    };

    const submitPoll = async () => {
      const payload = Object.assign({}, poll);
      payload.poll_type = props.poll_type;
      payload.poll_type = 2;
      payload.group = news.group;
      payload.release_date = news.release_date;

      var pollTranslations = {};
      tenant.value.languages.forEach((lang) => {
        pollTranslations[lang.code] = {
          title: news.translations[lang.code].title + " - poll",
        };
      });
      payload.translations = pollTranslations;

      payload.poll_questions.forEach((question) => {
        let questionTranslations = {};
        Object.keys(question.translations).forEach((tr) => {
          if (question.translations[tr].question) {
            questionTranslations[tr] = question.translations[tr];
          }
        });
        question.translations = questionTranslations;

        if (question.question_type == 1) {
          question.question_options.forEach((option) => {
            let optionTranslations = {};

            Object.keys(option.translations).forEach((tr) => {
              if (option.translations[tr].title) {
                optionTranslations[tr] = option.translations[tr];
              }
            });

            option.translations = optionTranslations;
          });
        }
      });

      const response = await adminApi.savePoll(payload);
      return response;
    };

    const removeNews = async () => {
      if (confirm(i18n.t("Haluatko varmasti poistaaa uutisen?"))) {
        const response = await adminApi.deleteNews(news.id);
        if (response.ok) {
          router.replace({ name: "admin-news-list" });
        } else {
          alert(response.error);
        }
      }
    };

    const saveAttachment = async (file) => {
      if (file) {
        if (file.size > MAX_FILE_SIZE) {
          alert(i18n.t('Tiedoston maksimikoko on 10MB'));
          return;
        }

        isUploading.value = true;
        const response = await adminApi.saveNewsAttachment(file);

        if (response.status == 201) {
          news.attachments.push(response.data);
        }
        else {
          alert(response.error);
        }
        attachmentInput.value = "";
        isUploading.value = false;
      }
    };

    const downloadAttachment = async (attachment) => {
      const response = await adminApi.downloadNewsAttachment(
        news.id,
        attachment.id
      );

      if (response.ok) {
        download(response.data, attachment.name);
      } else {
        alert(response.error);
      }
    };

    const removeAttachment = async (attachment) => {
      news.attachments = news.attachments.filter(
        (a) => a.id !== attachment.id
      );
    };

    const onFileTypeMismatch = (file) => {
      alert(
        i18n.t(
          "Tiedostomuoto ei ole tuettu. Tuettuja tiedostomuotoja ovat jpeg sekä png."
        )
      );
    };

    const imageLoaded = () => {
      imageRemoved.value = false;
    };

    const removeImage = () => {
      news.image = null;
      imageRemoved.value = true;
      croppaEnabled.value = true;
    };


    const translate = async (fromLangCode, toLangs) => {
      translationErrors.value = [];

      if (langOk(news.translations, fromLangCode, ["text", "title"])) {
        isTranslating.value = true;
        const errors = await translateObjectTranslations(news.translations, fromLangCode, toLangs, ["text", "title"]);
        translationErrors.value = errors;
        forceUpdateTranslations();
        isTranslating.value = false;

        if (errors.length == 0) {
          translationCompleted.value = true;
        }

      }
      else {
        alert("Otsikko ja teksti on pakollinen ennen käännöstä");
      }
    }

    const validateLangs = () => {
      let valid = false;
      tenant.value.languages.forEach((lang) => {
        if (langOk(news.translations, lang.code, ["text", "title"]))
          valid = true;
      });

      if (valid) {
        return true;
      } else {
        return i18n.t("Otsikko tai teksti puuttuu");
      }
    };

    const validatePoll = (v) => {
      if (!hasPoll.value) return true;

      return poll.poll_questions.length > 0;
    };

    const validateQuestions = (v) => {
      if (!hasPoll.value) return true;

      let valid = true;
      poll.poll_questions.forEach((q) => {
        tenant.value.languages.forEach((lang) => {
          if (!q.translations[lang.code].question) valid = false;

          if (q.question_type == 1) {
            if (q.question_options.length == 0) {
              valid = false;
            } else {
              q.question_options.forEach((option) => {
                if (!option.translations[lang.code].title) {
                  valid = false;
                }
              });
            }
          }
        });
      });

      return valid;
    };

    const addQuestion = (type) => {
      let newQuestion = {
        id: null,
        question_type: type,
        translations: {},
        question_options: [],
      };
      tenant.value.languages.forEach((lang) => {
        newQuestion.translations[lang.code] = {
          question: "",
        };
      });
      poll.poll_questions.push(newQuestion);
      questionsTab.value = "tab-" + (poll.poll_questions.length - 1);
    };

    const removeQuestion = (index) => {
      poll.poll_questions.splice(index, 1);
    };

    const addOption = (question) => {
      let newOption = {
        id: null,
        translations: {},
      };
      tenant.value.languages.forEach((lang) => {
        newOption.translations[lang.code] = {
          title: "",
        };
      });
      question.question_options.push(newOption);
    };

    const removeOption = (question, index) => {
      question.question_options.splice(index, 1);
    };

    const getPageTitle = () => {
      const tenantTerm = getTenantTerm(
        "news",
        tenant.value.terms?.translations,
        i18n.locale
      );
      return tenantTerm ?? i18n.t("Uutiset");
    };

    onMounted(() => {
      if (props.id) {
        fetchNews(props.id);
      }
      else {
        var now = moment();
        news.release_date = now.format("DD.MM.YYYY HH:mm");
      }
    });

    const onEditorReady = (editor) => {
      // editor.getModule('toolbar').addHandler('image', () => imageHandler(editor));

    };

    const showCroppaControls = computed(() => {
      if (!myCroppa.value || Object.keys(myCroppa.value).length === 0) return false;

      return croppaEnabled.value && myCroppa.value.hasImage();
    })

    return {
      onEditorReady,
      showCroppaControls,
      getPageTitle,
      router,
      news,
      myCroppa,
      tenant,
      user,
      formIsValid,
      tab,
      form,
      editorOption,
      myCroppaInitialMedadata,
      hasVideo,
      hasPoll,
      translationErrors,
      poll,
      croppaEnabled,
      hasAnswers,
      removeNews,
      isUploading,
      onFileTypeMismatch,
      imageLoaded,
      removeImage,
      langOk,
      // parseVideoUrl,
      submit,
      removeNews,
      saveAttachment,
      downloadAttachment,
      removeAttachment,
      addQuestion,
      removeQuestion,
      addOption,
      translate,
      removeOption,
      validateLangs,
      inputRules,
      forceUpdateTranslationsKey,
      translationCompleted,
      isTranslating,
      isSubmitting,
      // modules,
      questionsTab,
      icons: {
        mdiCheck,
        mdiArrowRight,
        mdiBlockHelper,
        mdiClockOutline,
        mdiCalendar,
        mdiAttachment,
        mdiMap,
        mdiDeleteOutline,
        mdiPlus,
        mdiHandPointingUp,
        mdiChatOutline,
        mdiDeleteOutline,
      },
    };
  },
};
</script>


<style>
.newsform .ql-editor p {
  font-size: 1.2em;
  line-height: 1.8em;
}

.newsform .ql-editor {
  min-height: 200px;
}
</style>
